import { DateTime } from "luxon";
import type { Weekdays } from "../../../components/Pickers/WeekdayPicker/weekday-picker.component";
import type {
  ManagementDashboardQueryParams,
  ManagementDashboardResponse,
  ManagementDashboardStatusIdApi
} from "../typings/management-dashboard.types";
import type { ManagementDisplayUnit } from "../typings/management-display-unit";
import { formatManagementChartDayLabel } from "./management-period-picker.utils";
import {
  isManagementPeriodDateBookable,
  type ManagementBookingWeekendSettings
} from "./management-period-weekend.utils";

export const MANAGEMENT_STATUS_SERIES_KEYS: ManagementDashboardStatusIdApi[] = [
  "office",
  "mobileWork",
  "notSpecified"
];

/** Inclusive max length aligned with weplace custom period limit. */
export const MANAGEMENT_USER_STATUS_CUSTOM_MAX_DAYS = 366;

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
] as const;

export type ManagementStatusWeekdayKey = (typeof WEEKDAY_ORDER)[number];

export type ManagementStatusGroupMode = "day" | "week" | "month";

export type ManagementStatusDaySeriesPoint = {
  date: string;
  dateLabel: string;
  weekday: ManagementStatusWeekdayKey;
  officeCount: number;
  officePercent: number;
  mobileWorkCount: number;
  mobileWorkPercent: number;
  notSpecifiedCount: number;
  notSpecifiedPercent: number;
};

export type ManagementStatusOverTimePoint = {
  label: string;
  officeCount: number;
  mobileWorkCount: number;
  notSpecifiedCount: number;
  officePercent: number;
  mobileWorkPercent: number;
  notSpecifiedPercent: number;
};

export type ManagementStatusWeekdaySeriesPoint = {
  weekday: ManagementStatusWeekdayKey;
  officeCount: number;
  mobileWorkCount: number;
  notSpecifiedCount: number;
  officePercent: number;
  mobileWorkPercent: number;
  notSpecifiedPercent: number;
};

function weekdayKeyFromIsoDate(isoDate: string): ManagementStatusWeekdayKey | null {
  const day = DateTime.fromISO(isoDate);
  if (!day.isValid) {
    return null;
  }
  return WEEKDAY_ORDER[day.weekday - 1] ?? null;
}

function isWeekdayEnabled(weekday: ManagementStatusWeekdayKey, weekdays: Weekdays): boolean {
  return weekdays[weekday];
}

function toPercentParts(office: number, mobileWork: number, notSpecified: number) {
  const total = office + mobileWork + notSpecified;
  if (total === 0) {
    return { officePercent: 0, mobileWorkPercent: 0, notSpecifiedPercent: 0 };
  }
  return {
    officePercent: Math.round((office / total) * 100),
    mobileWorkPercent: Math.round((mobileWork / total) * 100),
    notSpecifiedPercent: Math.round((notSpecified / total) * 100)
  };
}

/**
 * Builds query params for work-status detail from an inclusive start/end range.
 * Empty departmentIds = all departments.
 */
export function buildManagementUserStatusQueryParams(input: {
  rangeStart: DateTime;
  rangeEnd: DateTime;
  departmentIds: string[];
}): ManagementDashboardQueryParams {
  const periodStart = input.rangeStart.startOf("day").toISODate() ?? "";
  const periodEnd = input.rangeEnd.startOf("day").toISODate() ?? "";

  const params: ManagementDashboardQueryParams = {
    periodMode: "custom",
    periodStart,
    periodEnd
  };

  if (input.departmentIds.length > 0) {
    params.departmentIds = input.departmentIds;
  }

  return params;
}

/**
 * Month grain only when the selected range spans more than one calendar month.
 */
export function isManagementStatusMonthGrainAvailable(
  rangeStart: DateTime,
  rangeEnd: DateTime
): boolean {
  const start = rangeStart.startOf("day");
  const end = rangeEnd.startOf("day");
  if (!start.isValid || !end.isValid || end < start) {
    return false;
  }
  return end.startOf("month") > start.startOf("month");
}

/**
 * Maps API status days, applying company weekend rules and the weekday toolbar filter.
 */
export function mapStatusesPeriodDaysForUserStatusDetail(
  days: ManagementDashboardResponse["statusesPeriodDays"],
  weekdays: Weekdays,
  weekendSettings: ManagementBookingWeekendSettings = {},
  language = "en"
): ManagementStatusDaySeriesPoint[] {
  const points: ManagementStatusDaySeriesPoint[] = [];

  for (const day of days) {
    if (!isManagementPeriodDateBookable(day.date, weekendSettings)) {
      continue;
    }
    const weekday = weekdayKeyFromIsoDate(day.date);
    if (!weekday || !isWeekdayEnabled(weekday, weekdays)) {
      continue;
    }
    points.push({
      date: day.date,
      dateLabel: formatManagementChartDayLabel(day.date, language),
      weekday,
      officeCount: day.office.count,
      officePercent: day.office.percent,
      mobileWorkCount: day.mobileWork.count,
      mobileWorkPercent: day.mobileWork.percent,
      notSpecifiedCount: day.notSpecified.count,
      notSpecifiedPercent: day.notSpecified.percent
    });
  }

  return points;
}

/**
 * Aggregates daily status counts into weekday buckets (sum counts, then derive %).
 */
export function aggregateStatusesByWeekday(
  days: ManagementStatusDaySeriesPoint[]
): ManagementStatusWeekdaySeriesPoint[] {
  const byWeekday = new Map<
    ManagementStatusWeekdayKey,
    { officeCount: number; mobileWorkCount: number; notSpecifiedCount: number }
  >();

  for (const day of days) {
    const existing = byWeekday.get(day.weekday) ?? {
      officeCount: 0,
      mobileWorkCount: 0,
      notSpecifiedCount: 0
    };
    existing.officeCount += day.officeCount;
    existing.mobileWorkCount += day.mobileWorkCount;
    existing.notSpecifiedCount += day.notSpecifiedCount;
    byWeekday.set(day.weekday, existing);
  }

  return WEEKDAY_ORDER.flatMap(weekday => {
    const totals = byWeekday.get(weekday);
    if (!totals) {
      return [];
    }
    const percents = toPercentParts(
      totals.officeCount,
      totals.mobileWorkCount,
      totals.notSpecifiedCount
    );
    return [
      {
        weekday,
        ...totals,
        ...percents
      }
    ];
  });
}

function formatWeekBucketLabelFromRange(
  rangeStart: DateTime,
  rangeEnd: DateTime,
  language: string
): string {
  const start = rangeStart.setLocale(language);
  const end = rangeEnd.setLocale(language);
  if (start.hasSame(end, "day")) {
    return start.toFormat("d LLL");
  }
  if (start.hasSame(end, "month")) {
    return `${start.toFormat("d")} – ${end.toFormat("d LLL")}`;
  }
  return `${start.toFormat("d LLL")} – ${end.toFormat("d LLL")}`;
}

function formatMonthBucketLabel(monthStart: DateTime, language: string): string {
  return monthStart.setLocale(language).toFormat("LLL yyyy");
}

function emptyStatusCounts() {
  return { officeCount: 0, mobileWorkCount: 0, notSpecifiedCount: 0 };
}

/**
 * Re-buckets filtered daily points for the over-time chart (day / week / month grain).
 * Percents are always derived from summed counts in each bucket.
 * Week labels use the first–last day present in each week (e.g. "6 – 10 Jul").
 */
export function aggregateStatusesOverTime(
  days: ManagementStatusDaySeriesPoint[],
  groupMode: ManagementStatusGroupMode,
  language = "en"
): ManagementStatusOverTimePoint[] {
  if (groupMode === "day") {
    return days.map(day => ({
      label: day.dateLabel,
      officeCount: day.officeCount,
      mobileWorkCount: day.mobileWorkCount,
      notSpecifiedCount: day.notSpecifiedCount,
      officePercent: day.officePercent,
      mobileWorkPercent: day.mobileWorkPercent,
      notSpecifiedPercent: day.notSpecifiedPercent
    }));
  }

  type Bucket = {
    firstDate: string;
    lastDate: string;
    officeCount: number;
    mobileWorkCount: number;
    notSpecifiedCount: number;
  };

  const byBucket = new Map<string, Bucket>();

  for (const day of days) {
    const date = DateTime.fromISO(day.date);
    if (!date.isValid) {
      continue;
    }
    const bucketStart = groupMode === "week" ? date.startOf("week") : date.startOf("month");
    const key = bucketStart.toISODate() ?? day.date;
    const existing = byBucket.get(key) ?? {
      firstDate: day.date,
      lastDate: day.date,
      ...emptyStatusCounts()
    };
    if (day.date < existing.firstDate) {
      existing.firstDate = day.date;
    }
    if (day.date > existing.lastDate) {
      existing.lastDate = day.date;
    }
    existing.officeCount += day.officeCount;
    existing.mobileWorkCount += day.mobileWorkCount;
    existing.notSpecifiedCount += day.notSpecifiedCount;
    byBucket.set(key, existing);
  }

  return [...byBucket.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, totals]) => {
      const monthStart = DateTime.fromISO(key);
      const label =
        groupMode === "week"
          ? formatWeekBucketLabelFromRange(
              DateTime.fromISO(totals.firstDate),
              DateTime.fromISO(totals.lastDate),
              language
            )
          : formatMonthBucketLabel(monthStart, language);
      return {
        label,
        officeCount: totals.officeCount,
        mobileWorkCount: totals.mobileWorkCount,
        notSpecifiedCount: totals.notSpecifiedCount,
        ...toPercentParts(totals.officeCount, totals.mobileWorkCount, totals.notSpecifiedCount)
      };
    });
}

export function getUserStatusOverTimeChartRows(
  points: ManagementStatusOverTimePoint[],
  unit: ManagementDisplayUnit
): Array<{
  label: string;
  office: number;
  mobileWork: number;
  notSpecified: number;
}> {
  return points.map(point => {
    if (unit === "percent") {
      return {
        label: point.label,
        office: point.officePercent,
        mobileWork: point.mobileWorkPercent,
        notSpecified: point.notSpecifiedPercent
      };
    }
    return {
      label: point.label,
      office: point.officeCount,
      mobileWork: point.mobileWorkCount,
      notSpecified: point.notSpecifiedCount
    };
  });
}

export function getUserStatusWeekdayChartRows(
  days: ManagementStatusWeekdaySeriesPoint[],
  unit: ManagementDisplayUnit
): Array<{
  weekday: ManagementStatusWeekdayKey;
  office: number;
  mobileWork: number;
  notSpecified: number;
}> {
  return days.map(day => {
    if (unit === "percent") {
      return {
        weekday: day.weekday,
        office: day.officePercent,
        mobileWork: day.mobileWorkPercent,
        notSpecified: day.notSpecifiedPercent
      };
    }
    return {
      weekday: day.weekday,
      office: day.officeCount,
      mobileWork: day.mobileWorkCount,
      notSpecified: day.notSpecifiedCount
    };
  });
}
